import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useSelector } from 'react-redux';
import { selectUserRoles } from '../../../redux/slices/userSlice';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { createCSOFeedbackClient } from './csoFeedbackService';
import { usePermissions } from '../../../hooks/usePermissions';

const blank = () => ({ Title: '', Notes: '', Status: 'Open' });

export default function CSOFeedback({ projectId }) {
  const { canView, canEdit } = usePermissions();
  if (!canView('portal.forms.cso-feedback')) return null;
  return <CSOFeedbackContent projectId={projectId} allowEdit={canEdit('portal.forms.cso-feedback')} />;
}

function CSOFeedbackContent({ projectId, allowEdit }) {
  const { instance, accounts } = useMsal();
  const currentAccount = instance.getActiveAccount() || accounts[0];
  // MSAL can return a new account object on each cache read. Preserve the
  // reference for the same identity so loading state does not recreate the
  // client and trigger another fetch. Real account changes still reload.
  const accountRef = useRef(currentAccount);
  const accountKeys = ['homeAccountId', 'localAccountId', 'tenantId', 'username', 'name'];
  if (Boolean(accountRef.current) !== Boolean(currentAccount)
    || accountKeys.some(key => accountRef.current?.[key] !== currentAccount?.[key])) {
    accountRef.current = currentAccount;
  }
  const account = accountRef.current;
  const roles = useSelector(selectUserRoles);
  const canChangeStatus = allowEdit && (roles?.includes('Apps') || false);
  const client = useMemo(() => createCSOFeedbackClient(instance, account, fetch, canChangeStatus), [instance, account, canChangeStatus]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [show, setShow] = useState(false);
  const [existing, setExisting] = useState(null);
  const [values, setValues] = useState(blank);
  const [files, setFiles] = useState([]);
  const [removals, setRemovals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [uncertain, setUncertain] = useState(false);
  const canEditDetails = allowEdit && (!existing || existing.canEditDetails === true);
  const canSave = canEditDetails || canChangeStatus;
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    client.list(projectId).then(data => { if (active) setItems(data); })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, projectId, revision]);
  function open(item) {
    if (!item && !allowEdit) return;
    setExisting(item || null);
    setValues(item ? { Title: item.Title || '', Notes: item.Notes || '', Status: item.Status || '' } : blank());
    setFiles([]); setRemovals([]); setSavedId(null); setSaveError(''); setUncertain(false); setShow(true);
  }
  function close() {
    if (busy) return;
    setShow(false);
    if (savedId || uncertain) setRevision(value => value + 1);
  }
  function chooseFiles(event) {
    const next = [...files, ...Array.from(event.target.files || [])];
    event.target.value = '';
    const names = [...(existing?.AttachmentFiles || []).filter(file => !removals.includes(file.FileName)).map(file => file.FileName), ...next.map(file => file.name)];
    if (new Set(names.map(name => name.toLowerCase())).size !== names.length) {
      setSaveError('Attachment filenames must be unique. Remove the existing attachment before replacing it.'); return;
    }
    if (next.some(file => /["*:<>?\\/|#%]/.test(file.name) || file.name.length > 128)) {
      setSaveError('Use filenames of 128 characters or fewer without " * : < > ? \\ / | # %.'); return;
    }
    setSaveError(''); setFiles(next);
  }
  async function save(event) {
    event.preventDefault();
    if (busy || uncertain || !canSave) return;
    setBusy(true); setSaveError('');
    let id = savedId;
    try {
      if (!id) {
        id = await client.save(projectId, values, existing);
        setSavedId(id);
      }
      await client.attachments(id, files, removals,
        file => setFiles(current => current.filter(value => value !== file)),
        name => setRemovals(current => current.filter(value => value !== name)));
      setShow(false); setRevision(value => value + 1);
    } catch (err) {
      setUncertain(Boolean(err.uncertain));
      setSaveError(id ? `Submission saved, but attachments could not be completed. Retry attachments or close and reload. ${err.message}` : err.message);
    } finally { setBusy(false); }
  }
  return <>
    {error && <Alert variant="danger">{error}</Alert>}
    <div className="d-flex gap-2 mb-3">
      {allowEdit && <Button onClick={() => open()} disabled={!projectId || loading || !!error}>New CSO Feedback</Button>}
      <Button variant="outline-secondary" disabled={loading} onClick={() => setRevision(value => value + 1)}>Reload submissions</Button>
    </div>
    {loading ? <p role="status">Loading CSO feedback…</p> : !error && <Table responsive striped bordered>
      <thead><tr><th>Title</th><th>Status</th><th>Submitted by</th><th>Attachments</th><th>Actions</th></tr></thead>
      <tbody>{items.map(item => <tr key={item.Id}>
        <td>{item.Title}</td><td>{item.Status}</td><td>{item.SubmittedBy}</td><td>{item.AttachmentFiles.length}</td>
        <td><Button variant="link" onClick={() => open(item)}>{(allowEdit && item.canEditDetails) || canChangeStatus ? 'View / Edit' : 'View'}</Button></td>
      </tr>)}{!items.length && <tr><td colSpan={5}>No CSO feedback has been submitted for this job.</td></tr>}</tbody>
    </Table>}
    <Modal show={show} onHide={close} backdrop="static" size="lg">
      <Form onSubmit={save}>
        <Modal.Header closeButton={!busy}><Modal.Title>{existing ? canSave ? 'Edit' : 'View' : 'New'} CSO Feedback</Modal.Title></Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger">{saveError}</Alert>}
          <p>Project ID: {projectId} · Submitted by: {existing?.SubmittedBy || account?.name || account?.username}</p>
          <fieldset disabled={busy || !!savedId || uncertain}>
            <Form.Group controlId="cso-title" className="mb-3"><Form.Label>Title</Form.Label><Form.Control readOnly={!canEditDetails} required maxLength={255} value={values.Title} onChange={e => setValues({ ...values, Title: e.target.value })} /></Form.Group>
            <Form.Group controlId="cso-notes" className="mb-3"><Form.Label>Notes</Form.Label><Form.Control readOnly={!canEditDetails} as="textarea" rows={5} value={values.Notes} onChange={e => setValues({ ...values, Notes: e.target.value })} /></Form.Group>
            <Form.Group controlId="cso-status" className="mb-3"><Form.Label>Status</Form.Label><Form.Control required maxLength={255} readOnly={!existing || !canChangeStatus} value={values.Status} onChange={e => setValues({ ...values, Status: e.target.value })} /></Form.Group>
          </fieldset>
          <h6>Attachments</h6>
          {(existing?.AttachmentFiles || []).map(file => <div className="mb-2" key={file.FileName}>
            <a href={client.attachmentUrl(file)} target="_blank" rel="noopener noreferrer">{file.FileName}</a>{' '}
            {canEditDetails && <Button size="sm" variant="outline-danger" disabled={busy || !!savedId || uncertain} onClick={() => setRemovals(current => current.includes(file.FileName) ? current.filter(name => name !== file.FileName) : [...current, file.FileName])}>{removals.includes(file.FileName) ? 'Undo removal' : 'Remove'}</Button>}
          </div>)}
          {files.map(file => <div key={file.name}>{file.name}{' '}<Button variant="link" disabled={busy || !!savedId || uncertain} onClick={() => setFiles(current => current.filter(value => value !== file))}>Remove</Button></div>)}
          {canEditDetails && <Form.Group controlId="cso-attachments"><Form.Label>Add attachments</Form.Label><Form.Control type="file" multiple disabled={busy || !!savedId || uncertain} onChange={chooseFiles} /></Form.Group>}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" disabled={busy} onClick={close}>Close</Button>{canSave && <Button type="submit" disabled={busy || uncertain}>{busy ? 'Saving…' : savedId ? 'Retry attachments' : 'Save'}</Button>}</Modal.Footer>
      </Form>
    </Modal>
  </>;
}
